// Publishing's API calls, all in one module (P5-B01 publish, P5-B02 rollback and health; docs/API.md
// «Publish»; DATA_MODEL §7 `deployments`), typed by the generated schema.
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';
import { qk } from '@/api/queries';

export type DeploymentStatus = Schemas['DeploymentStatus'];
export type Channel = Schemas['PublishChannel'];
/** One publication (immutable except its status): `is_active` («Активна»), `can_rollback` («Откатить»), `failed_step`. */
export type DeploymentOut = Schemas['DeploymentOut'];
/** 202 of POST /admin/websites/{id}/publish. */
export type PublishStarted = Schemas['PublishOut'];
/** 202 of POST /admin/deployments/{id}/rollback: the `publish.rollback` job and the deployment that goes live again. */
export type RollbackStarted = Schemas['RollbackOut'];
/** The health card over the last 30 days of synthetic checks. */
export type SiteHealth = Schemas['SiteHealthOut'];

export const deploymentsKey = (siteId: string) => ['deployments', siteId] as const;
export const healthKey = (siteId: string) => ['deployments', siteId, 'health'] as const;

/** GET /admin/websites/{id}/deployments, newest first. Polls while one activates (the job's events move the tiles). */
export const deploymentsQuery = (siteId: string) =>
  queryOptions({
    queryKey: deploymentsKey(siteId),
    queryFn: async (): Promise<DeploymentOut[]> =>
      (await unwrap(api.GET('/admin/websites/{website_id}/deployments', { params: { path: { website_id: siteId }, query: { size: 50 } } }))).items,
    refetchInterval: (q) => (q.state.data?.some((d) => d.status === 'activating') ? 1500 : false),
    retry: false,
  });

/** GET /admin/websites/{id}/health: uptime, LCP p75, TLS expiry, CDN cache hit. */
export const healthQuery = (siteId: string) =>
  queryOptions({
    queryKey: healthKey(siteId),
    queryFn: () => unwrap(api.GET('/admin/websites/{website_id}/health', { params: { path: { website_id: siteId } } })),
    retry: false,
  });

/**
 * «Опубликовать в демо»: POST /admin/websites/{id}/publish {channel: demo} (production is P6-D01); the
 * revision defaults to the website's approved one. The `Idempotency-Key` is made per modal opening, so a
 * double click publishes once (P5-B01).
 */
export function publish(siteId: string, idempotencyKey: string, revisionId?: string | null): Promise<PublishStarted> {
  return unwrap(
    api.POST('/admin/websites/{website_id}/publish', {
      params: { path: { website_id: siteId }, header: { 'Idempotency-Key': idempotencyKey } },
      body: revisionId ? { channel: 'demo', revision_id: revisionId } : { channel: 'demo' },
    }),
  );
}

/**
 * «Переключить production» (P6-D01, TZ §3, §12.2): POST /admin/websites/{id}/publish {channel: production,
 * confirm_hostname} with the same idempotency key per opening. It needs `deployment:publish_production` (403
 * otherwise), a website past the handoff (`pending_production` or `production`) and a verified production
 * domain; the typed domain is the re-confirmation (422 `confirmation_required`, `params.hostname`). Once the production binding is
 * active the demo bindings take their policy (410 or a temporary 301).
 */
export function publishProduction(siteId: string, idempotencyKey: string, hostname: string): Promise<PublishStarted> {
  return unwrap(
    api.POST('/admin/websites/{website_id}/publish', {
      params: { path: { website_id: siteId }, header: { 'Idempotency-Key': idempotencyKey } },
      body: { channel: 'production', confirm_hostname: hostname },
    }),
  );
}

/** Why production publishing is not available yet, or null when it is (C10: the reason is shown, never hidden). */
export type ProductionBlock = 'forbidden' | 'stage' | 'domain';

export function productionBlock(opts: { allowed: boolean; status: string; domainStatus: string | null }): ProductionBlock | null {
  if (!opts.allowed) return 'forbidden';
  if (opts.status !== 'pending_production' && opts.status !== 'production') return 'stage';
  if (opts.domainStatus !== 'verified' && opts.domainStatus !== 'active') return 'domain';
  return null;
}

/**
 * «Откатить»: POST /admin/deployments/{id}/rollback → 202 with the `publish.rollback` job, which repoints the
 * website in one transaction and invalidates the caches; no rebuild (P5-B02, AC-13).
 */
export function rollback(deploymentId: string): Promise<RollbackStarted> {
  return unwrap(api.POST('/admin/deployments/{deployment_id}/rollback', { params: { path: { deployment_id: deploymentId } } }));
}

export const activeDeployment = (list: DeploymentOut[] | undefined, channel: Channel = 'demo') =>
  list?.find((d) => d.status === 'active' && d.channel === channel) ?? null;
export const runningDeployment = (list: DeploymentOut[] | undefined) => list?.find((d) => d.status === 'activating') ?? null;

/** The next publication number (the modal names it before the API does). */
export const nextVersion = (list: DeploymentOut[] | undefined) => (list ?? []).reduce((m, d) => Math.max(m, d.version), 0) + 1;

/** «B · r14» from a deployment's revision. */
export function revisionLabel(r: DeploymentOut['revision'] | null): string | null {
  if (!r) return null;
  return r.variant_key ? `${r.variant_key} · r${r.number}` : `r${r.number}`;
}

/** After a publish or rollback: the deployments, the health card and the website (status, lifecycle). */
export function refreshAfterDeploy(qc: QueryClient, siteId: string): void {
  void qc.invalidateQueries({ queryKey: deploymentsKey(siteId) });
  void qc.invalidateQueries({ queryKey: qk.website(siteId) });
  void qc.invalidateQueries({ queryKey: ['domains', siteId] });
  void qc.invalidateQueries({ queryKey: ['websites'] });
}

// ── The pipeline: the publish job's steps (P5-B01) on the design's four tiles ──

/** The job's steps in the order P5-B01 runs them. */
export const STEP_ORDER = ['check', 'render', 'upload', 'verify', 'activate', 'invalidate', 'health'] as const;
/** Which tile each step belongs to: 01 Generate, 02 Validate, 03 Upload, 04 Activate. */
const TILE_OF: Record<string, number> = { check: 0, render: 0, verify: 1, upload: 2, activate: 3, invalidate: 3, health: 3 };

export type TileState = 'done' | 'current' | 'pending' | 'failed';

/**
 * The four tiles' states. Without a running publish they show the live site: all done once something is
 * active, all pending before the first publish. While a job runs, a tile is done when every step of it ran
 * before the current one (P5-B01 verifies after the upload, so «Validate» completes after «Upload»).
 */
export function tileStates(opts: { running: boolean; step: string | null; failed?: boolean; anyActive: boolean }): TileState[] {
  const tiles = [0, 1, 2, 3];
  if (!opts.running) return tiles.map(() => (opts.anyActive ? 'done' : 'pending'));
  const at = opts.step ? STEP_ORDER.indexOf(opts.step as (typeof STEP_ORDER)[number]) : 0;
  const pos = at < 0 ? 0 : at;
  const current = TILE_OF[STEP_ORDER[pos] ?? 'check'] ?? 0;
  return tiles.map((tile) => {
    if (tile === current) return opts.failed ? 'failed' : 'current';
    const steps = STEP_ORDER.filter((s) => TILE_OF[s] === tile);
    return steps.every((s) => STEP_ORDER.indexOf(s) < pos) ? 'done' : 'pending';
  });
}
